import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import Tooltip from "@mui/material/Tooltip";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import { apiGetAllAkaun, apiSearchAkaun } from "api/akaun";

export function useAkaunData({
  onRowSelect,
  selectedRow,
  currentPage,
  itemsPerPage,
  search,
  refreshTrigger,
  visibleColumns = {
    nama: true,
    kodpelanggan: true,
    noakaun: true,
    kodharta: true,
    bahagianpemantau: true,
    statusakaun: true,
  },
  editingCell = null,
  editValue = '',
  onCellEdit = () => {},
  onSaveEdit = () => {},
  onCancelEdit = () => {},
  onEditValueChange = () => {},
  onEditKeyDown = () => {},
  saving = false,
  kodHartaOptions = [],
}) {
  const [akaunData, setAkaunData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  useEffect(() => {
    const fetchAkaun = async () => {
      try {
        setLoading(true);
        const hasSearchParams =
          search && Object.values(search).some((value) => value && value.trim() !== "");
        let response;
        if (hasSearchParams) {
          const searchParams = {};
          if (search.nama && search.nama.trim()) searchParams.nama = search.nama.trim();
          if (search.no_kp && search.no_kp.trim()) searchParams.no_kp = search.no_kp.trim();
          if (search.nama_syarikat && search.nama_syarikat.trim())
            searchParams.nama_syarikat = search.nama_syarikat.trim();
          if (search.kod_pelanggan && search.kod_pelanggan.trim())
            searchParams.kod_pelanggan = search.kod_pelanggan.trim();
          if (search.no_akaun && search.no_akaun.trim())
            searchParams.no_akaun = search.no_akaun.trim();
          if (search.zon && search.zon.trim()) searchParams.zon = search.zon.trim();
          if (search.kategori_kutipan && search.kategori_kutipan.trim())
            searchParams.kategori_kutipan = search.kategori_kutipan.trim();
          if (search.status_akaun && search.status_akaun.trim())
            searchParams.status_akaun = search.status_akaun.trim();
          response = await apiSearchAkaun(searchParams, currentPage, itemsPerPage);
        } else {
          response = await apiGetAllAkaun(currentPage, itemsPerPage);
        }

        if (response && response.data) {
          setAkaunData(response.data);
          setTotalPages(response.pagination.totalPages);
          setTotalItems(response.pagination.totalItems);
        } else if (Array.isArray(response)) {
          setAkaunData(response);
          setTotalPages(1);
          setTotalItems(response.length);
        } else {
          setAkaunData([]);
          setTotalPages(1);
          setTotalItems(0);
        }
      } catch (error) {
        setAkaunData([]);
        setTotalPages(1);
        setTotalItems(0);
      } finally {
        setLoading(false);
      }
    };
    fetchAkaun();
  }, [currentPage, itemsPerPage, refreshTrigger]);

  const Nama = ({ name }) => (
    <MDBox display="flex" alignItems="center" lineHeight={1}>
      <MDTypography variant="caption" fontWeight="medium" ml={0.05} lineHeight={1}>
        {name}
      </MDTypography>
    </MDBox>
  );

  // Prop types for internal component to satisfy ESLint
  Nama.propTypes = {
    name: PropTypes.string,
  };

  Nama.defaultProps = {
    name: "",
  };

  const generateRows = () => {
    if (loading) {
      return [
        {
          select: (
            <input type="checkbox" checked={false} disabled style={{ cursor: "not-allowed" }} />
          ),
          nama: <Nama name="Loading..." />,
          kod_pelanggan: (
            <MDTypography variant="caption" color="text" fontWeight="medium">
              ...
            </MDTypography>
          ),
          no_akaun: (
            <MDTypography variant="caption" color="text" fontWeight="medium">
              ...
            </MDTypography>
          ),
          kod_harta: (
            <MDTypography variant="caption" color="text" fontWeight="medium">
              Loading...
            </MDTypography>
          ),
          bahagian_pemantau: (
            <MDTypography variant="caption" color="text" fontWeight="medium">
              Loading...
            </MDTypography>
          ),
          status_akaun: (
            <MDTypography variant="caption" color="text" fontWeight="medium">
              Loading...
            </MDTypography>
          ),
        },
      ];
    }
    let filteredData = akaunData;
    return filteredData.map((akaun) => {
      const rowId = akaun.id || akaun.no_akaun;
      const row = {
        select: (
          <input
            type="checkbox"
            checked={selectedRow && selectedRow.id === akaun.id}
            onChange={() => onRowSelect && onRowSelect({ ...akaun, kodharta: akaun.kod_harta })}
            style={{ cursor: "pointer" }}
          />
        ),
        nama: editingCell?.rowId === rowId && editingCell?.column === 'nama' ? (
          <MDBox display="flex" alignItems="center" gap={0.5}>
            <TextField
              value={editValue}
              onChange={(e) => onEditValueChange(e.target.value)}
              onKeyDown={onEditKeyDown}
              size="small"
              autoFocus
              disabled={saving}
              fullWidth
              sx={{
                '& .MuiInputBase-input': {
                  fontSize: '0.875rem',
                  py: 0.5,
                }
              }}
            />
            <IconButton size="small" onClick={onSaveEdit} disabled={saving} color="success">
              <CheckIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={onCancelEdit} disabled={saving} color="error">
              <CloseIcon fontSize="small" />
            </IconButton>
          </MDBox>
        ) : (
          <MDBox
            display="flex"
            alignItems="center"
            gap={1}
            sx={{
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                borderRadius: 1,
              },
              px: 1,
              py: 0.5,
            }}
            onClick={() => onCellEdit(rowId, 'nama', akaun.nama, akaun)}
          >
            <MDTypography variant="caption" fontWeight="medium">
              {akaun.nama || "Unknown"}
            </MDTypography>
            <EditIcon sx={{ fontSize: 14, color: 'action.disabled', opacity: 0.5 }} />
          </MDBox>
        ),
        kod_pelanggan: editingCell?.rowId === rowId && editingCell?.column === 'kod_pelanggan' ? (
          <MDBox display="flex" alignItems="center" gap={0.5}>
            <TextField
              value={editValue}
              onChange={(e) => onEditValueChange(e.target.value)}
              onKeyDown={onEditKeyDown}
              size="small"
              autoFocus
              disabled={saving}
              fullWidth
              sx={{
                '& .MuiInputBase-input': {
                  fontSize: '0.875rem',
                  py: 0.5,
                }
              }}
            />
            <IconButton size="small" onClick={onSaveEdit} disabled={saving} color="success">
              <CheckIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={onCancelEdit} disabled={saving} color="error">
              <CloseIcon fontSize="small" />
            </IconButton>
          </MDBox>
        ) : (
          <MDBox
            display="flex"
            alignItems="center"
            gap={1}
            sx={{
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                borderRadius: 1,
              },
              px: 1,
              py: 0.5,
            }}
            onClick={() => onCellEdit(rowId, 'kod_pelanggan', akaun.kod_pelanggan, akaun)}
          >
            <MDTypography variant="caption" color="text" fontWeight="medium">
              {akaun.kod_pelanggan || "-"}
            </MDTypography>
            <EditIcon sx={{ fontSize: 14, color: 'action.disabled', opacity: 0.5 }} />
          </MDBox>
        ),
        no_akaun: (
          <MDTypography variant="caption" color="text" fontWeight="medium">
            {akaun.no_akaun || "-"}
          </MDTypography>
        ),
        kod_harta: editingCell?.rowId === rowId && editingCell?.column === 'kod_harta' ? (
          <MDBox display="flex" alignItems="center" gap={0.5}>
            <Select
              value={editValue}
              onChange={(e) => onEditValueChange(e.target.value)}
              onKeyDown={onEditKeyDown}
              size="small"
              autoFocus
              disabled={saving}
              sx={{
                minWidth: 120,
                fontSize: '0.875rem',
                '& .MuiSelect-select': { py: 0.5 }
              }}
            >
              {kodHartaOptions.map((kod) => (
                <MenuItem key={kod} value={kod}>{kod}</MenuItem>
              ))}
            </Select>
            <IconButton size="small" onClick={onSaveEdit} disabled={saving} color="success">
              <CheckIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={onCancelEdit} disabled={saving} color="error">
              <CloseIcon fontSize="small" />
            </IconButton>
          </MDBox>
        ) : (
          <MDBox
            display="flex"
            alignItems="center"
            gap={1}
            sx={{
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                borderRadius: 1,
              },
              px: 1,
              py: 0.5,
            }}
            onClick={() => onCellEdit(rowId, 'kod_harta', akaun.kod_harta, akaun)}
          >
            <MDTypography
              variant="caption"
              color="text"
              fontWeight="medium"
              sx={{
                '&:hover': {
                  color: 'info.main',
                  textDecoration: 'underline',
                },
              }}
            >
              {akaun.kod_harta || "-"}
            </MDTypography>
            <EditIcon sx={{ fontSize: 14, color: 'action.disabled', opacity: 0.5 }} />
          </MDBox>
        ),
        bahagian_pemantau: (
          <MDTypography variant="caption" color="text" fontWeight="medium">
            {akaun.bahagian_pemantau || "-"}
          </MDTypography>
        ),
        status_akaun: editingCell?.rowId === rowId && editingCell?.column === 'status_akaun' ? (
          <MDBox display="flex" alignItems="center" gap={0.5}>
            <Select
              value={editValue}
              onChange={(e) => onEditValueChange(e.target.value)}
              onKeyDown={onEditKeyDown}
              size="small"
              autoFocus
              disabled={saving}
              sx={{
                minWidth: 120,
                fontSize: '0.875rem',
                '& .MuiSelect-select': { py: 0.5 }
              }}
            >
              <MenuItem value="Aktif">Aktif</MenuItem>
              <MenuItem value="Tidak Aktif">Tidak Aktif</MenuItem>
            </Select>
            <IconButton size="small" onClick={onSaveEdit} disabled={saving} color="success">
              <CheckIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={onCancelEdit} disabled={saving} color="error">
              <CloseIcon fontSize="small" />
            </IconButton>
          </MDBox>
        ) : (
          <MDBox
            display="flex"
            alignItems="center"
            gap={1}
            sx={{
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                borderRadius: 1,
              },
              px: 1,
              py: 0.5,
            }}
            onClick={() => onCellEdit(rowId, 'status_akaun', akaun.status_akaun, akaun)}
          >
            <MDTypography
              variant="caption"
              fontWeight="medium"
              style={{
                color:
                  akaun.status_akaun === "Aktif"
                    ? "green"
                    : akaun.status_akaun === "Tidak Aktif"
                    ? "red"
                    : undefined,
              }}
            >
              {akaun.status_akaun || "-"}
            </MDTypography>
            <EditIcon sx={{ fontSize: 14, color: 'action.disabled', opacity: 0.5 }} />
          </MDBox>
        ),
      };
      row.kodharta = akaun.kod_harta;
      Object.keys(akaun).forEach((key) => {
        if (!(key in row)) row[key] = akaun[key];
      });
      return row;
    });
  };

  // Define all columns with visibility flags
  const allColumns = [
    { Header: "", accessor: "select", width: "5%", align: "center", headerAlign: "center", alwaysVisible: true },
    { Header: "nama", accessor: "nama", width: "15%", align: "left", headerAlign: "center", visible: visibleColumns.nama },
    {
      Header: "kod pelanggan",
      accessor: "kod_pelanggan",
      width: "12%",
      align: "center",
      headerAlign: "center",
      visible: visibleColumns.kodpelanggan,
    },
    {
      Header: "no akaun",
      accessor: "no_akaun",
      width: "12%",
      align: "center",
      headerAlign: "center",
      visible: visibleColumns.noakaun,
    },
    {
      Header: "kod harta",
      accessor: "kod_harta",
      width: "13%",
      align: "center",
      headerAlign: "center",
      visible: visibleColumns.kodharta,
    },
    {
      Header: "bahagian pemantau",
      accessor: "bahagian_pemantau",
      width: "15%",
      align: "center",
      headerAlign: "center",
      visible: visibleColumns.bahagianpemantau,
    },
    {
      Header: "status akaun",
      accessor: "status_akaun",
      width: "15%",
      align: "center",
      headerAlign: "center",
      visible: visibleColumns.statusakaun,
    },
  ];

  // Filter columns based on visibility
  const filteredColumns = allColumns.filter(col => col.alwaysVisible || col.visible !== false);

  return {
    columns: filteredColumns,
    rows: generateRows(),
    pagination: {
      totalPages,
      totalItems,
      currentPage,
      actualItemsOnPage: akaunData.length,
    },
    loading,
  };
}
